/**
 * EventMetricTrend 组件类型定义
 */

import type { IViewportFullscreenApi } from '@/pages/dashboard/components/ViewportFullscreen/types';
import { CSSProperties } from 'react';

/**
 * Radio 选项配置
 */
export interface RadioOption {
  /** 选项值 */
  value: string;
  /** 中文标签 */
  labelCn: string;
  /** 英文标签 */
  labelEn: string;
  /** 指标数据键名 */
  metricKey: string;
  /** 日期数据键名 */
  dateKey: string;
  /** 中文名称（用于图表 series） */
  cnName?: string;
  /** 英文名称（用于图表 series） */
  enName?: string;
  /** 指定使用的事件数据源键名（如 'eventData' 或 'lastEventData'） */
  eventDataKey?: string;
  /** 多目标系列替换配置数组，与 radioConfig.targetSeriesIndexes 按位对应 */
  replacements?: Array<{
    metricKey?: string;
    dateKey?: string;
    cnName?: string;
    enName?: string;
    tooltipEnName?: string;
    tooltipCnName?: string;
  }>;
}

/**
 * Radio 组件配置
 */
export interface RadioConfig {
  /** 需要动态替换的目标 series 索引 */
  targetSeriesIndex: number;
  /** 多系列替换时与 replacements 按位对应的索引列表 */
  targetSeriesIndexes?: number[];
  /** 默认选中的值 */
  defaultValue: string;
  /** Radio 选项列表 */
  options: RadioOption[];
}

/**
 * 图表格式化规则
 */
export interface FormatRule {
  /** 格式化类型（如 'number', 'percent', 'currency' 等） */
  type?: string;
  /** 小数位数 */
  decimals?: number;
  /** 单位 */
  unit?: string;
  /** 是否使用千分位 */
  useGrouping?: boolean;
}

/**
 * 系列配置
 */
export interface SeriesConfig {
  /** 中文名称 */
  cnName: string;
  /** 英文名称 */
  enName: string;
  /** 指标数据键名 */
  metricKey: string;
  /** 日期数据键名 */
  dateKey: string;
  /** 系列颜色 */
  color?: string;
  /** 格式化规则 */
  formatRule?: FormatRule;
  /** 自定义系列配置 */
  customSeriesConfig?: Record<string, unknown>;
  /** 异常点标记颜色 */
  abnormalSymbolColor?: string;
  /** 为 true 时该系列不在浮窗(tooltip)中展示 */
  hideInTooltip?: boolean;
  /** 浮窗中展示的英文系列名；不配置则与图例/折线名称一致 */
  tooltipEnName?: string;
  /** 浮窗中展示的中文系列名；不配置则与图例/折线名称一致 */
  tooltipCnName?: string;
  /** 异动数据子键，用于匹配 anomalyResult 中的 sub-key（如 'd1_roas'） */
  anomalySubKey?: string;
  /**
   * 维度接口返回的原始 code（如 ua_network 的 value），
   * 供 KpiEventDimensionTrendService 用 selectorMap 映射图例中英文
   */
  dimRawCode?: string;
  /** 为 true 时不展示图例维度图标（特例系列） */
  suppressLegendDimensionIcon?: boolean;
  /** 其他配置 */
  [key: string]: unknown;
}

/**
 * 事件层：将同一日期下某 event_type 的多条合并为一条展示，并追加总条数后缀
 */
export interface EventTypeCollapseConfig {
  /** 与单条事件的 event_type 匹配的主键（与 matchEventTypes 二选一或并存） */
  matchEventType: string;
  /**
   * 多条任一命中即参与合并（如 `matchEventType` 为「实验」时，另含「Experiment」等别名）；
   * 未配时仅用 `matchEventType`。
   */
  matchEventTypes?: string[];
  /** 中文后缀，`{count}` 替换为匹配条数 */
  totalSuffixCn: string;
  /** 英文后缀，`{count}` 替换为匹配条数 */
  totalSuffixEn: string;
}

/** 实验类事件气泡主文案固定为 A/B 实验（与 eventTypeCollapse.matchEventType 联动） */
export interface ExperimentTypeFixedLabelParams {
  enabled?: boolean;
  labelCn?: string;
  labelEn?: string;
}

/**
 * KPI 配色对象：`background` / `fontColor`
 * - **按类型分组的 `eventTypeBarColorMap`**：`TKpiEventTypeColorEntry.xAxisBubble` 仅表示 **浮窗内该类型 Tag**；
 * - **X 轴旁事件条数数字气泡**：用 `chartConfig.kpiXAxisEventCountBubble`（中性），不用类型上的 xAxisBubble。
 */
export interface TKpiXAxisBubbleLabelColors {
  background?: string;
  fontColor?: string;
  borderColor?: string;
  emphasisBackground?: string;
  emphasisFontColor?: string;
  emphasisBorderColor?: string;
}

/** `eventTooltipsMap` 下单事件类型：舆情条 / EventDetailsPop Tag / 写入 event_bar_* */
export interface TKpiEventTooltipColorEntry {
  background?: string;
  fontColor?: string;
  /**
   * 与 `chartConfig.eventTypeTagStyleAliases[key]` 合并，用于 Tag 命中接口 `event_type.name_en` 等。
   */
  tagStyleAliases?: string[];
}

/** 旧版扁平：类型名 → 色串或 { background, fontColor } */
export type TEventTypeBarColorValue =
  | string
  | {
    background?: string;
    fontColor?: string;
    /**
     * 与 `chartConfig.eventTypeTagStyleAliases[key]` 合并（扁平 map 时）。
     */
    tagStyleAliases?: string[];
  };

/**
 * KPI 按事件类型分组：
 * - **eventTooltips**：舆情左侧条、`event_bar_*` 及「未配 xAxisBubble 时」浮窗 Tag 的底色来源；色串或 `{ background, fontColor }`。
 * - **xAxisBubble**：仅作用于 **浮窗 EventTypeTag**（以及合并舆情条配色逻辑）；**不再**驱动 X 轴旁数字气泡——轴上条数样式用 `chartConfig.kpiXAxisEventCountBubble`。
 * - **tagStyleAliases**：与本类型 map key 对应的接口英文名别名，与 `chartConfig.eventTypeTagStyleAliases` 同学 key 时合并。
 */
export interface TKpiEventTypeColorEntry {
  xAxisBubble?: TKpiXAxisBubbleLabelColors;
  eventTooltips?: TEventTypeBarColorValue;
  /**
   * 与 `chartConfig.eventTypeTagStyleAliases[key]` 合并。
   * 英文名别名可与接口 `name_en` **忽略大小写**命中，无需为每种大小写各写一条。
   */
  tagStyleAliases?: string[];
}

/**
 * KPI 顶层拆分：`xAxisBubble` + `eventTooltipsMap`（兼容旧写法）。
 */
export interface TKpiEventTypeBarColorMap {
  xAxisBubble?: TKpiXAxisBubbleLabelColors;
  eventTooltipsMap?: Record<string, string | TKpiEventTooltipColorEntry>;
}

/** 旧版保留键：仅底部 X 轴气泡（迁移期） */
export const EVENT_TYPE_BAR_COLOR_MAP_KEY_KPI_X_AXIS_BUBBLE =
  '__kpiXAxisBubble__';

/** chartConfig.eventTypeBarColorMap：按类型分组 / 顶层拆分 / 旧扁平 */
export type TEventTypeBarColorMapConfig =
  | TKpiEventTypeBarColorMap
  | Record<string, TEventTypeBarColorValue | TKpiEventTypeColorEntry>;

/**
 * 图表区：按 `event_type` 展示文案扩展「实验」类合并词表（与 `matchesConfiguredEventTypeLabel` 一致）。
 * KPI 事件卡省略时由 `resolveEventTypeCollapseConfig` 默认对齐类型键「实验」；多语言/别名时再配 `matchNameCn` / `matchNameEn`。
 */
export interface ChartExperimentEventMatchConfig {
  /** 为 false 时不从本配置推导合并规则 */
  enabled?: boolean;
  matchNameCn?: string[];
  matchNameEn?: string[];
}

/** 图例维度图标解析来源（与 chartConfig.legendIconSource 一致） */
export type TLegendIconSource = 'system' | 'ua_network' | 'country';

/** 见 `ChartConfig.eventStartTimeAxisClip` */
export type TEventStartTimeAxisClipMode = 'before_min' | 'within_range';

/**
 * 图表配置
 */
export interface ChartConfig {
  /**
   * 事件 `start_time` 相对当前图 X 轴刻度过滤（`EventMetricTrend` 内与 `xAxisData` 对齐）。
   * - `before_min`（默认）：去掉早于「最早刻度日」的事件，**不**裁晚于最晚刻度的（事件区间可长于趋势线）。
   * - `within_range`：仅保留落在 [最早刻度, 最晚刻度] 内。
   * - `false`：关闭裁剪，原样展示接口事件数据。
   */
  eventStartTimeAxisClip?: TEventStartTimeAxisClipMode | false;
  /** Y 轴间隔 */
  interval?: number;
  /**
   * 覆盖 ECharts grid（会与默认的 containLabel 等合并）。
   * 用于单卡微调留白，避免轴标签或数据标签贴边被裁切。
   */
  grid?: {
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
    containLabel?: boolean;
  };
  /** Y 轴刻度与轴线间距（ECharts axisLabel.margin）；不设则不带该字段 */
  yAxisLabelMargin?: number;
  /** Y 轴格式化规则 */
  yAxisFormat?: FormatRule;
  /** 日期格式化规则 */
  dateFormatRule?: string;
  /**
   * 全屏时 X 轴字号放大倍数（含 axisLabel / rich 等 xAxis 内 fontSize，
   * 以及 tooltip.axisPointer.label、React tooltip 的 --dbd-tooltip-scale）。
   * 优先级高于 ViewportFullscreen / 面板级 fullscreenXAxisScale；未配置时回退 fsScale。
   */
  fullscreenXAxisScale?: number;
  /** 数据格式化规则 */
  formatRule?: FormatRule;
  /**
   * 是否显示折线数据点（symbol）。false 时不展示圆点，仅保留折线。
   * KpiEventDimensionTrendService 默认会设为 false。
   */
  showSymbol?: boolean;
  /**
   * 是否显示折线数据标签（addChartLabel）。false 时不展示数值标签。
   * 默认 true，KpiEventDimensionTrendService 图表一般设为 false。
   */
  showDataLabel?: boolean;
  /**
   * 事件气泡：指定 event_type 多日多条时合并为一条并带总条数；未配置则保持原列表
   */
  eventTypeCollapse?: EventTypeCollapseConfig;
  /**
   * 同一日期下事件气泡从上到下展示顺序：数组越靠前优先级越高（与 event_type 全等匹配）。
   * 未配置或空数组则保持接口/原列表顺序。
   */
  eventTypeDisplayOrder?: string[];
  /**
   * 当日事件条数大于阈值时，点击单条气泡跳转事件详情页；未配置则不跳转
   */
  eventBubbleDetailsNav?: EventBubbleDetailsNavConfig;
  /**
   * 可跳转详情时舆情 EventWindows 会为 `.dbo-event-item--details-nav` 加下划线；设为 false 时
   * 仅在 dashboard 本组件样式内覆盖去掉下划线，不修改舆情源码。默认 true（保持原样）。
   */
  eventBubbleItemUnderline?: boolean;
  /**
   * KPI：`{ 版更: { xAxisBubble, eventTooltips }, … }` 按类型分组；或顶层 `{ xAxisBubble, eventTooltipsMap }`；或旧扁平/保留键。
   */
  eventTypeBarColorMap?: TEventTypeBarColorMapConfig;
  /**
   * KPI EventDetailsPop Tag 命中：chartConfig 级别名表，`eventTypeBarColorMap` 类型 key → `event_type.name_en` 等。
   * 可与「分组对象」上的 `tagStyleAliases` 合并；不设则仅用分组内或顶层扁平对象上的别名。
   */
  eventTypeTagStyleAliases?: Record<string, string[]>;
  /**
   * KPI：仅 **X 轴日期旁事件条数** 小气泡（ECharts label），建议中性灰白。
   * 与 `eventTypeBarColorMap` 内各类型 `xAxisBubble`（浮窗 EventTypeTag）分离。
   * 省略时走 hook 默认（白底 `#fff`、字色 `#9B9FB0`），一般无需在 pageConfig 写常量。
   */
  kpiXAxisEventCountBubble?: TKpiXAxisBubbleLabelColors;
  /**
   * 可选：扩展「实验」类合并匹配（如英文类型名）。省略时 KPI 默认仅按 `event_type` 键「实验」合并。
   */
  chartEventExperimentTooltip?: ChartExperimentEventMatchConfig;
  /**
   * 为 true 时，`event_type` 与 `eventTypeCollapse.matchEventType` 全等的事件，
   * 气泡主文案固定为 `experimentTypeFixedLabelCn` / `experimentTypeFixedLabelEn`（多条合并时仍追加条数后缀）。
   * 默认 false。未配置 `eventTypeCollapse` 时不生效。
   */
  useExperimentTypeFixedDisplayName?: boolean;
  /** 固定中文文案，默认「A/B 实验」 */
  experimentTypeFixedLabelCn?: string;
  /** 固定英文文案，默认「A/B Test」 */
  experimentTypeFixedLabelEn?: string;
  /** 是否显示 Y 轴轴线，默认 false */
  showYAxisLine?: boolean;
  /**
   * 为 true 时，带 dimRawCode 的维度折线在图例色点与名称之间展示小图标（平台 / 买量渠道 / 国旗）。
   * 无 dimRawCode 的系列不处理，不影响异动数据合并（mergeAnomaly 仅改 is_abnormal，不增系列）。
   */
  showLegendDimensionIcons?: boolean;
  /**
   * 非组合维度时的图标解析类型；组合维度（dimRawCode 含 `_`）优先用 legendIconCompositeKeys 分段解析
   */
  legendIconSource?: TLegendIconSource;
  /**
   * 与 dimensionLegendCompositeKeys 一致：`a_b` 时前段/后段分别按对应 key 解析图标（如 system + country）
   */
  legendIconCompositeKeys?: [string, string];
  /**
   * 轴触发浮窗时对 formatter 节流（毫秒）。0 或未设不节流。
   * 多折线时 tooltip 走 ReactDOMServer.renderToString，mousemove 过频易卡。
   */
  tooltipThrottleMs?: number;
  /**
   * KPI：图表区 EventWindows 浮动事件条是否对「连续自然日重复事件」做合并（减少同一事件多日多条标签）。
   * 非 `false` 且 `kpiEventUi` 启用时默认开启。X 轴数字气泡与底部 EventDetailsPop 始终按未合并逐日计数/列表。
   */
  mergeConsecutiveTrendDaysForChartEventWindows?: boolean;
  /**
   * KPI 概览专用：X 轴事件数字行与底部悬浮详情等（不影响未配置的页面如异动）。
   * `true` 等价于 `{ enabled: true, xAxisRow: true }`；**事件设置齿轮默认关闭**，需要时传 `settings: true`。
   */
  // 注意：kpiEventUi 放开会展示x轴事件气泡，但是 这个做了一半，功能不全，有些 bug，产品临时说不要了，后续需要再继续 开发
  kpiEventUi?:
  | boolean
  | {
    /** 默认 true */
    enabled?: boolean;
    /** 事件设置齿轮与弹窗；默认 false，仅显式 `true` 时展示 */
    settings?: boolean;
    /** X 轴数字气泡行 + 底部悬浮详情，默认 true */
    xAxisRow?: boolean;
    /**
     * 轴触发 tooltip 中系列行左侧圆点颜色（按 series 下标）；
     * 仅 KPI EventMetricTrend 读取，用于与折线/系列色统一；未设则仍用 ECharts 的 item.color。
     * 可与 `eventTypeBarColorMap` 中各类型背景色配置成同一套色值。
     */
    tooltipSeriesIconColors?: Record<number, string>;
  };
}

/**
 * 事件气泡跳转事件分析详情（/eventAnalysis/eventDetails）
 */
export interface EventBubbleDetailsNavConfig {
  /**
   * 当日事件条数严格大于该值时才允许点击跳转（「三个以上」即默认 3，至少 4 条）。
   * 与 `eventTypeCollapse` 同时使用时，由 EventMetricTrend 按合并前当日数组长度判断（多条实验合并为一条展示仍按多条计）。
   */
  minCountExclusive?: number;
  /** 路由片段，默认 `eventAnalysis/eventDetails` */
  path?: string;
  /**
   * 合并进 URL query（`date`、`fromPage`、`timeType`、`dashboardType` 由运行时注入，可被此处覆盖）
   */
  query?: Record<string, string>;
}

/**
 * 下载列配置
 */
export interface DownloadColumn {
  /** 列键名 */
  key: string;
  /** 列标题 */
  title: string;
  /** 关联的 Radio 值（可选，用于动态过滤列） */
  radioValue?: string;
  /** 数据索引 */
  dataIndex?: string;
  /** 格式化规则 */
  formatRule?: FormatRule;
}

/**
 * 下载配置
 */
export interface DownloadConfig {
  /** 文件名 */
  fileName?: string;
  /** Sheet 名称 */
  sheetName?: string;
  /** 列配置 */
  columns?: DownloadColumn[];
}

/**
 * Y 轴最小值配置
 */
export interface YAxisMinConfig {
  /** 是否启用 */
  enabled: boolean;
  /** 最小值比例（相对于数据范围） */
  minRatio?: number;
  /** 禁用时使用的静态最小值（enabled=false 时生效） */
  min?: number;
}

/**
 * 事件数据项
 */
export interface EventDataItem {
  /** 事件名称 */
  name: string;
  /** 事件中文名称 */
  name_cn: string;
  /** 事件中文描述 */
  event_desc_cn: string;
  /** 事件英文描述 */
  event_desc_en: string;
  /** 左侧色条颜色（如由 chartConfig.eventTypeBarColorMap 注入） */
  event_bar_color?: string;
  /** 左侧条旁主文案颜色（由 eventTypeBarColorMap.fontColor 注入；舆情 EventItem 可选） */
  event_bar_font_color?: string;
  /** 其他事件属性 */
  [key: string]: unknown;
}

/**
 * 事件数据映射（日期 -> 事件列表）
 */
export interface EventDataMap {
  [date: string]: EventDataItem[];
}

/**
 * 原始事件数据
 */
export interface PropsEventData {
  /** 默认事件数据 */
  eventData: EventDataMap;
  /** 去年同期事件数据 */
  lastEventData?: EventDataMap;
  /** 原始事件列表 */
  rawEventList: RawEventItem[];
  /** 支持动态扩展其他事件数据源 */
  [key: string]: EventDataMap | RawEventItem[] | undefined;
}

/**
 * 原始事件项
 */
export interface RawEventItem {
  /** 事件 ID */
  event_id?: string;
  /** 事件中文标题 */
  event_title_cn?: string;
  /** 事件英文标题 */
  event_title_en?: string;
  /** 开始时间 */
  start_time?: string;
  /** 结束时间 */
  end_time?: string;
  /** 其他属性 */
  [key: string]: unknown;
}

/**
 * 系列数据项
 */
export interface SeriesDataItem {
  /** 日期 */
  date?: string;
  /** 是否异常点 */
  is_abnormal?: boolean;
  /** 动态指标数据 */
  [metricKey: string]: unknown;
}

/**
 * 图表数据
 */
export interface ChartData {
  /** X 轴数据（日期数组） */
  xAxisData: string[];
  /** 系列数据 */
  seriesData: SeriesDataItem[];
}

/**
 * API 数据包装
 */
export interface ApiDataWrapper<T> {
  /** 数据 */
  data?: T;
  /** 错误信息 */
  errorMsg?: string;
  /** 加载状态 */
  loading?: boolean;
}

/**
 * 组件数据源
 */
export interface ComponentData {
  /** 趋势图数据 */
  trendData?: ApiDataWrapper<ChartData>;
  /** 事件数据 */
  eventData?: ApiDataWrapper<PropsEventData>;
  /** 支持动态扩展其他数据源 */
  [key: string]: ApiDataWrapper<unknown> | undefined;
}

/**
 * Tooltip 参数
 */
export interface TooltipParams {
  /** 数据值 */
  value?: number;
  /** 数据对象 */
  data?: {
    /** 值 */
    value?: number;
    /** 其他属性 */
    [key: string]: unknown;
  };
  /** 系列类型 */
  seriesType?: string;
  /** 系列名称 */
  seriesName?: string;
  /** 系列索引 */
  seriesIndex: number;
  /** 数据索引 */
  dataIndex: number;
  /** 颜色 */
  color?: string;
}

/**
 * Tooltip 数据项
 */
export interface TooltipDataItem {
  /** 系列名称 */
  name: string;
  /** 格式化后的值 */
  value: string | number;
  /** 颜色 */
  color?: string;
  /** 日期 */
  date?: string;
}

/**
 * ECharts Option 类型（简化版）
 */
export interface EChartsOption {
  /** Y 轴配置 */
  yAxis?: unknown[];
  /** X 轴配置 */
  xAxis?: unknown[];
  /** 系列配置 */
  series?: EChartsSeries[];
  /** 图例配置 */
  legend?: {
    /** 图例数据 */
    data?: string[];
    /** 其他配置 */
    [key: string]: unknown;
  };
  /** 网格配置 */
  grid?: {
    /** 上边距 */
    top?: number;
    /** 左边距 */
    left?: number;
    /** 右边距 */
    right?: number;
    /** 下边距 */
    bottom?: number;
  };
  /** 其他配置 */
  [key: string]: unknown;
}

/**
 * ECharts 系列配置
 */
export interface EChartsSeries {
  /** 系列名称 */
  name?: string;
  /** 系列类型 */
  type?: string;
  /** 系列数据 */
  data?: (number | SeriesDataPoint)[];
  /** 其他配置 */
  [key: string]: unknown;
}

/**
 * 系列数据点
 */
export interface SeriesDataPoint {
  /** 值 */
  value: number;
  /** 样式配置 */
  itemStyle?: {
    /** 颜色 */
    color?: string;
    /** 边框颜色 */
    borderColor?: string;
    /** 透明度 */
    opacity?: number;
  };
  /** 标记类型 */
  symbol?: string;
  /** 标记大小 */
  symbolSize?: number;
}

/**
 * EventMetricTrend 组件 Props
 */
export interface EventMetricTrendProps {
  /** 中文标题 */
  titleCn: string;
  /** 英文标题 */
  titleEn: string;
  /** 子元素 */
  children?: React.ReactNode;
  /** 自定义类名 */
  className?: string;
  /** 自定义样式 */
  style?: CSSProperties;
  /** 副标题（英文） */
  subTitle?: string;
  /** 副标题（中文） */
  subTitleCn?: string;
  /** 数据源 */
  data: ComponentData;
  /** 系列配置 */
  seriesConfig: SeriesConfig[];
  /** 双柱状图配置（预留） */
  doubleBarChartConfig?: Record<string, unknown>;
  /** API 数据键名 */
  apiDataKey?: string;
  /** 事件数据键名 */
  eventKey?: string;
  /** 图表配置 */
  chartConfig?: ChartConfig;
  /** 默认是否显示事件 */
  defaultShowEvent?: boolean;
  /** 是否显示事件开关 */
  showEventSwitch?: boolean;
  /** Radio 配置 */
  radioConfig?: RadioConfig;
  /** 跳转链接 */
  link?: string;
  /** 下载配置 */
  downloadConfig?: DownloadConfig;
  /** Y 轴最小值配置 */
  yAxisMin?: YAxisMinConfig;
  // ===== 扩展功能 props =====
  /** 隐藏控件（事件开关/Radio/下载），保留标题和AI副标题 */
  hideControls?: boolean;
  /** 外部控制事件显示状态（覆盖内部 state） */
  controlledShowEvent?: boolean;
  /** 外部控制 Radio 选择值（覆盖内部 state） */
  controlledRadioValue?: string;
  /** AI Tag，提供时调用 useAiSummary 显示动态副标题 */
  aiTag?: string;
  /** 按当前 Radio 值覆盖 AI Tag，例如自然日对齐使用 *_natural */
  aiTagMap?: Record<string, string | undefined>;
  /** true 时按当前 Radio 值读取 req.data_keys / req.ai_analysis */
  enableAlignmentAiDataKey?: boolean;
  /** 对齐方式 → data_keys 字段名，覆盖 DEFAULT_ALIGNMENT_DATA_KEY_MAP */
  alignmentDataKeyMap?: Record<string, string | undefined>;
  /**
   * 为 true 时，图表区使用 CardBox + renderList 在折线与表格间切换（与 MetricsTrendCo 等一致）。
   * KPI 概览在 pageConfig 中对需要的 EventMetricTrend 显式设为 true；与 hideControls 同时为真时表格仍关闭。
   */
  showTableView?: boolean;
  /** 表格 Tab 内表格区域高度，默认与折线区域一致 */
  tableViewHeight?: number;
  /**
   * 异常状态（加载中 / 无数据 / 加载失败）容器高度。
   * 未设置时自动根据 tableViewHeight 计算（非表格模式默认 385）。
   */
  abnormalHeight?: number;
  /**
   * perf: 开启视口可见性优化。为 true 时，不在视口内的图表只渲染轻量占位，
   * 滚入视口后才渲染完整 ECharts + EventWindows。默认 false，不影响其他页面。
   */
  enableViewportOptimization?: boolean;
  /**
   * 折线图区域高度（像素）。不传时非表格模式下默认 375（与历史行为一致）。
   * 用于全屏等场景按需拉高图表。
   */
  trendChartHeight?: number;
  /**
   * 由 ViewportFullscreenShell 注入；开启后卡片右上角展示视口全屏入口（可与 hideControls 同时存在）。
   */
  viewportFullscreen?: IViewportFullscreenApi;
  /** 其他属性 */
  [key: string]: unknown;
}

// ========== hooks / kpiEventUiUtils / 子组件补充类型 ==========

/** useEventMetricTrendKpiEventDetailsPop：单日事件气泡数据 */
export type TKpiEventPopDay = {
  events: number;
  date: string;
  event_list: unknown[];
};

/** useEventMetricTrendKpiEventDetailsPop 选项 */
export type TUseKpiEventDetailsPopOptions = {
  seriesId?: string;
  xAxisBubbleLabelColors?: TKpiXAxisBubbleLabelColors | null;
};

/** EventMetricTrend 事件设置弹窗 confirm 载荷 */
export interface IEventSettingConfirmPayload {
  bubbleEvent: Record<string, unknown[]>;
  showEventAxis: boolean;
  showEventBubbles: boolean;
  /**
   * 为 true 时图表事件浮窗内实验类多条按条平铺；false 时按配置合并为「A/B 实验（n）」等汇总展示。
   */
  showExperimentEventDetailFlat: boolean;
}

/** EventMetricTrendEventSettingModal Props */
export interface IEventMetricTrendEventSettingModalProps {
  visible: boolean;
  close: () => void;
  confirm: (data: IEventSettingConfirmPayload) => void;
  /** 大类 → 事件列表（与舆情一致） */
  data: Record<string, unknown[]>;
  timeFormat: string;
  showEventAxisValue: boolean;
  showEventBubblesValue: boolean;
  /** 与 chartEventSetting 同源；默认 false 为合并展示实验类 */
  showExperimentEventDetailFlatValue: boolean;
  dateType: 'daily' | 'weekly' | 'monthly' | 'hourly';
  xAxisData: string[];
  /** 事件列表接口加载中；为 true 时弹窗主体展示加载态 */
  requestLoading?: boolean;
  /** 事件列表接口失败；为 true 时弹窗主体展示失败态 */
  requestError?: boolean;
  /** 为 true 时隐藏「显示事件轴/气泡」勾选（由 KPI 筛选条控制） */
  hideAxisBubbleToggles?: boolean;
  /** 当前已生效的 bubbleEvent（按 X 轴时间分组）；用于打开弹窗时恢复勾选态 */
  bubbleEventValue?: Record<string, unknown[]>;
  /**
   * 用户是否已在弹窗点过「确定」写入 bubbleEvent。
   * true 时即使 bubbleEvent 为空也视为「全不选」，而非「默认全选」。
   */
  bubbleSelectionCommitted?: boolean;
}

/** 事件设置弹窗文案（组件内 languageMap，不依赖全局 intl） */
export interface IEventSettingModalStrings {
  caption: string;
  tips: string;
  showEventAxis: string;
  showEventBubbles: string;
  showExperimentEventDetailFlat: string;
  emptyNoEvents: string;
  setDisplayEvents: string;
  clearAll: string;
  selectAll: string;
  event: string;
  confirm: string;
  cancel: string;
}

/** EventMetricTrendAxisDetailsPop：X 轴悬浮层当前展示数据 */
export type TEventAxisDetailsPopShowList = {
  count: number;
  event_list: Record<string, EventDataItem[]>;
  time: string;
};

/** useEventMetricTrendKpiEventUi：图表区事件开关与气泡筛选状态 */
export interface IKpiChartEventSetting {
  bubbleEvent: EventDataMap;
  showEventAxis: boolean;
  showEventBubbles: boolean;
  /** 实验类在浮窗内平铺展示；默认 false 为合并汇总文案 */
  showExperimentEventDetailFlat?: boolean;
}

/** formatEventDataSource 合并事件列时 columns 元素最小形状 */
export type TEventMergeColumnCfg = {
  dependKey: string;
  code: string;
  dateKey: string;
};

/** useEventMetricTrendTable：表格列定义 */
export type TTrendTableColumnOption = {
  key: string;
  header: string;
  align: 'left' | 'right';
  width?: number;
  minWidth?: number;
  /** 为 true 时导出对该列启用自动换行 */
  wrapText?: boolean;
};

export type TTrendTableDownloadPack = {
  option: TTrendTableColumnOption[];
  data: Record<string, string>[];
  downloadData: Record<string, unknown>[];
};

export type TTrendTableModel = {
  tableOption: TTrendTableColumnOption[];
  tableList: Record<string, string>[];
  downloadPack: TTrendTableDownloadPack;
};

/** useEventMetricTrendTable 入参 */
export interface IUseEventMetricTrendTableParams {
  effectiveShowTableView: boolean;
  seriesConfig: SeriesConfig[] | undefined;
  seriesData: SeriesDataItem[] | undefined;
  radioConfig: RadioConfig | undefined;
  effectiveRadioValue: string;
  isEN: boolean;
  formatMessage: (descriptor: { id: string; }) => string;
  rawEventList?: unknown[] | undefined;
  downloadColumnsResolved?: Array<Record<string, unknown>> | undefined;
}

/** getTrendTableSeriesMeta 输出的列元数据 */
export type TTrendTableSeriesMetaColumn = {
  colKey: string;
  header: string;
  metricKey: string;
  dateKey: string;
  formatRule?: FormatRule;
};

/** applyLegendDimensionIcons：折线 series 最小形状 */
export type TLegendLineSeriesLike = { name?: string; };

/** chartRenderEventList：日期 → 当日事件分组统计 */
export type TChartRenderEventList = Record<
string,
{
  count: number;
  event_list: Record<string, EventDataItem[]>;
}
>;

/** EventDetailsPop 所需 event_list 分组行 */
export type TEventDetailsPopGroupedRow = {
  event_type: {
    id?: string | number;
    name_en: string;
    name_cn?: string;
  };
  events: number;
  event_record: EventDataItem[];
};

// ========== utils.ts 工具函数所用的原始/格式化事件数据类型 ==========

/**
 * 后端事件记录的原始形态：start_time / event_type 等字段均为可选，兼容多种接口返回。
 * 以 Record<string, unknown> 的窄化别名替代 any，既保留字段可扩展性又获得类型检查。
 */
export type TRawEventRecord = Record<string, unknown> & {
  id?: string | number;
  event_type?: string;
  eventType?: string;
  event_title_en?: string;
  event_title_cn?: string;
  start_time?: string;
  end_time?: string;
  name?: string;
  name_cn?: string;
};

/**
 * 后端事件接口顶层一项（按日期聚合的一天）
 */
export interface IRawEventDayItem {
  date: string;
  event_list?: Array<{
    event_record?: TRawEventRecord[];
  }>;
  [key: string]: unknown;
}

/**
 * formatEventData 返回值：与 rawEventList 相似结构，但仅保留下载展示所需字段
 */
export interface IFormattedEventRecord {
  name?: string;
  name_cn?: string;
  start_time?: string;
  end_time?: string;
}

/**
 * formatEventDataSource 中下载列配置的最小字段结构
 */
export interface IDownloadColumnForEventMatch {
  type?: string;
  dependKey?: string;
  code?: string;
  dateKey?: string;
  eventDisplay?: string;
  [key: string]: unknown;
}
